import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

export type View =
  | 'docs'
  | 'settings'
  | 'statistics'
  | 'rates'
  | 'referral'
  | 'agency-order'
  | 'requests-list'
  | 'request-info'

export type NavParams = Record<string, string | undefined>

export type NavState = {
  view: View
  params: NavParams
}

export type NavigateOptions = {
  params?: NavParams
}

type NavContextValue = {
  state: NavState
  navigate: (view: View, options?: NavigateOptions) => void
  back: () => void
  getViewState: (view: View) => Record<string, unknown> | undefined
  setViewState: (view: View, data: Record<string, unknown>) => void
}

const PARENT_VIEW: Record<View, View | null> = {
  docs: 'requests-list',
  settings: 'requests-list',
  statistics: 'requests-list',
  rates: 'requests-list',
  referral: 'requests-list',
  'agency-order': 'settings',
  'requests-list': null,
  'request-info': 'requests-list',
}

type ViewStates = Partial<Record<View, Record<string, unknown>>>

const INITIAL_STATE: NavState = { view: 'requests-list', params: {} }

const NavContext = createContext<NavContextValue | null>(null)

export const NavProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<NavState>(INITIAL_STATE)
  const [viewStates, setViewStates] = useState<ViewStates>({})

  const navigate = useCallback((view: View, options?: NavigateOptions) => {
    setState({ view, params: options?.params ?? {} })
  }, [])

  const back = useCallback(() => {
    setState((prev) => {
      const parent = PARENT_VIEW[prev.view]
      if (!parent) {
        return prev
      }
      return { view: parent, params: {} }
    })
  }, [])

  const getViewState = useCallback(
    (view: View) => viewStates[view],
    [viewStates],
  )

  const setViewState = useCallback(
    (view: View, data: Record<string, unknown>) =>
      setViewStates((prev) => ({ ...prev, [view]: data })),
    [],
  )

  const value = useMemo<NavContextValue>(
    () => ({ state, navigate, back, getViewState, setViewState }),
    [state, navigate, back, getViewState, setViewState],
  )

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>
}

export const useNav = (): NavContextValue => {
  const ctx = useContext(NavContext)
  if (!ctx) {
    throw new Error('useNav must be used within NavProvider')
  }
  return ctx
}
